import { useEffect, useRef, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import {
  useGetEnrollmentHYCSDocumentVerificationData,
  useSubmitEnrollmentHYCSDocumentVerificationData,
  useSubmitEnrollmentHYCSDocVerificationCheckStatusData,
} from '@egw/api'
import { Button, Alert, AlertType, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { DocumentVerificationStatus } from '@egw/api/src/models/enrollment/enrollment'
import { ROUTES as HYCS_ROUTES } from '../../../constants/routes/routes-hycs'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - DocumentVerification')

const INTERVAL_TO_POLL_SUBMIT_ENROLLMENT = 5000

export const DocumentVerificationPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data, isLoading, error } = useGetEnrollmentHYCSDocumentVerificationData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSDocumentVerificationData()
  const { mutate: checkStatus, error: checkStatusError } = useSubmitEnrollmentHYCSDocVerificationCheckStatusData()

  const [isPending, setIsPending] = useState(false)
  const [verifyDocumentUrl, setVerifyDocumentUrl] = useState<string>()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = async (): Promise<void> => {
    try {
      const response = await mutate({
        triggerDocumentVerification: true,
      })

      const docStatus = response?.enrollmentData?.documentVerificationStatus
      const docUrl = response?.enrollmentData?.documentVerificationUrl

      if (docStatus === DocumentVerificationStatus.Pending) {
        setIsPending(true)
        setVerifyDocumentUrl(docUrl)
        window.open(docUrl, '_blank')
      } else {
        // void navigate(HYCS_ROUTES.ERROR.path)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onPollSubmit = async (): Promise<void> => {
    try {
      const response = await checkStatus({})

      if (response?.__typename === 'SubmitEnrollmentError') {
        switch (response.type) {
          case 'APPLICANT_IN_MANUAL_REVIEW':
            return void navigate(HYCS_ROUTES.MANUAL_REVIEW.path)
          case 'USER_UNABLE_TO_ENROLL':
            return void navigate(ROUTES.DECLINE.path)
          default:
            return void navigate(ROUTES.ERROR.path)
        }
      }

      if (
        response?.__typename === 'SubmitEnrollmentSuccess' &&
        response.enrollmentData?.documentVerificationStatus === DocumentVerificationStatus.Complete
      ) {
        setIsPending(false)

        if (response.path) {
          void navigate(response.path)
        } else {
          // TODO: Handle the case when path is not returned
          // void navigate(ROUTES.HOME.path)
        }
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  useEffect(() => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_DOCV_STARTED, {
      applicationId,
    })
  }, [])

  useEffect(() => {
    if (!isPending) {
      return
    }

    intervalRef.current = setInterval(() => {
      void onPollSubmit()
    }, INTERVAL_TO_POLL_SUBMIT_ENROLLMENT)

    return () => {
      const intervalId = intervalRef.current
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isPending])

  return (
    <EnrollmentHYCSLayout title={t('hycs.doc-verification.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <div className="w-full flex-1 flex flex-col justify-start lg:justify-center">
            <PageHeader title={t('hycs.doc-verification.title')} className="mb-xl" />

            {!isPending ? (
              <>
                <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                  {t('hycs.doc-verification.why_required_title')}
                </p>

                <p className="typo-primary-sm-regular text-text-meta mb-xl">
                  {t('hycs.doc-verification.why_required_description')}
                </p>

                <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                  {t('hycs.doc-verification.is_info_secure_title')}
                </p>

                <p className="typo-primary-sm-regular text-text-meta mb-xl">
                  {t('hycs.doc-verification.is_info_secure_description')}
                </p>

                <p className="typo-primary-lg-semibold text-text-header mb-sm">
                  {t('hycs.doc-verification.how_verification_work')}
                </p>

                <p className="typo-primary-sm-semibold text-text-header mb-2xs">
                  {t('hycs.doc-verification.photograph_id_title')}
                </p>

                <p className="typo-primary-sm-regular text-text-meta mb-sm">
                  {t('hycs.doc-verification.photograph_id_description')}
                </p>

                <p className="typo-primary-sm-semibold text-text-header mb-2xs">
                  {t('hycs.doc-verification.take_a_selfie')}
                </p>

                <p className="typo-primary-sm-regular text-text-meta mb-xl">
                  {t('hycs.doc-verification.take_a_selfie_description')}
                </p>
              </>
            ) : null}

            {isPending ? (
              <>
                <p className="typo-primary-base-regular text-text-meta mb-xl">{t('hycs.doc-verification.thank_you')}</p>
                <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                  {t('hycs.doc-verification.complete_verification')}
                </p>
                <a
                  href={verifyDocumentUrl}
                  target="_blank"
                  className="typo-primary-base-regular text-text-action mb-xl"
                  rel="noopener"
                >
                  {verifyDocumentUrl}
                </a>
                <p className="typo-primary-base-regular text-text-meta mb-xl">
                  {t('hycs.doc-verification.complete_verification_process')}
                </p>
              </>
            ) : null}

            <Alert
              alertType={AlertType.Info}
              description={t('hycs.doc-verification.complete_alert')}
              className="mb-xl"
            />

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isLoading={isSubmitting || isPending}
              onClick={() => void onSubmit()}
              testId="primaryAction_button"
            >
              {isPending
                ? t('hycs.doc-verification.verification_in_progress')
                : t('hycs.doc-verification.start_verification')}
            </Button>
          </div>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {(error ?? checkStatusError) ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
